/**
 * One invoice, as the person being asked to pay it reads it.
 *
 * The portal has listed invoices since it existed and every one of them was a number with nothing
 * behind it — which answers the one question a tenant opens this to ask with silence, and is why a
 * charge an agency took the trouble to name was invisible to the only person it was addressed to.
 * `GET /portal/invoices` now sends each invoice's lines and this is where they are read. ADR-0120.
 *
 * **Nothing here decides what a charge is called.** The name printed on a line is the `description`
 * the server sent, and the server's own fallback for a line that carries none is `chargeLabel()` —
 * the one function that knows "Other" is a default rather than a blank. A `label ?? typeName`
 * written on this page would be a second copy of that rule, and the day somebody changed a charge's
 * type it would rename a caution deposit on an invoice the tenant is holding. ADR-0110.
 *
 * **The file is the exception to that, and it is a route of its own.** `GET /portal/invoices/:id.pdf`
 * draws the tenant's own copy through the same `invoicePdf()` the agency's document route uses, so
 * there is one composition of an invoice and the two copies cannot drift. It is fetched with the
 * bearer token rather than linked to, for the reason written over `download()` below.
 *
 * **There is no `GET /portal/invoices/:id`, and this does not want one.** The list is already
 * filtered to this person in this agency, already carries the lines, and is small — a tenant has a
 * handful of invoices, not a ledger. Picking the one being looked at out of that answer means one
 * request instead of two and, more usefully, means this screen cannot show an invoice the account
 * screen would not: there is exactly one query behind both. An id that is not in the answer is not
 * theirs, and the screen says so in the same words the rest of the portal uses.
 */

import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { balanceOf, formatDate, formatMoney } from "@rentorng/core";
import { Alert, Badge, Button, Card, CardTitle, Detail, EmptyState, Spinner, Table } from "@/components/ui";
import { IconInvoice } from "@/components/icons";
import { usePageTitle } from "@/lib/title";
import { PortalProvider, usePortal, type PortalMe } from "@/lib/portal";
import { PortalShell } from "./PortalShell";

/* ------------------------------------------------------------------ the shapes */

/**
 * One row of an invoice.
 *
 * `description` is what the invoice said on the day it was sent — the agency's own words, put there
 * by `chargeLabel()` when the invoice was built. It is a string on every line the server sends,
 * because the server fills a blank one rather than leaving the reader to.
 */
export interface InvoiceLine {
  type: string;
  description: string;
  amount: number;
  periodStart: string | null;
  periodEnd: string | null;
}

export interface PortalInvoiceRow {
  id: string;
  number: string;
  status: string;
  issueDate?: string;
  dueDate: string;
  total: number;
  paid: number;
  payToken: string | null;
  unitLabel: string | null;
  propertyName: string | null;
  lines?: InvoiceLine[];
}

/* --------------------------------------------------------------- small pieces */

/**
 * What an invoice is made of, drawn as its lines.
 *
 * Exported because the account screen wants the same rows under the same headings — one drawing of
 * a breakdown rather than two that can drift apart, which is the same reason the naming itself
 * lives in one function on the server.
 *
 * **A period is printed only where the line carries one and has not already said so.** The rent line
 * is raised with the period written into its description — that is what the invoice said on the day,
 * and it is not this screen's to rewrite — so printing the same dates again in a column beside it
 * gives a tenant the same fact twice in two formats and invites them to look for the difference.
 * Most other lines carry no period at all, and "—" repeated down a column teaches somebody to stop
 * reading the column, so where nothing is left to say the column is not drawn.
 */
function periodOf(line: InvoiceLine): [string, string] | null {
  if (!line.periodStart && !line.periodEnd) return null;
  /* Already in the words the invoice was raised with. Matched on the stored day rather than on the
     formatted one, because the description carries whatever the builder wrote and only the raw
     `YYYY-MM-DD` is certain to be in it. */
  if (line.periodStart && line.description.includes(line.periodStart)) return null;
  return [line.periodStart ?? "", line.periodEnd ?? ""];
}

export function InvoiceLineTable({ lines, money }: { lines: InvoiceLine[]; money: (minor: number) => string }) {
  if (!lines.length) {
    return <EmptyState title="This invoice carries no breakdown." hint="Ask the agency what it is for." />;
  }

  const anyPeriod = lines.some((line) => periodOf(line) !== null);

  return (
    /*
      Marked so it can be found from outside.

      `verify-charge-label.mjs` reads the names out of *this* table rather than out of the page,
      because a search of the whole body would pass on any stray occurrence of the word "Other" —
      and the word "Other" is exactly what a broken reader prints. The attribute is how the suite
      names the table it means; it is load-bearing, not decoration.
    */
    <div data-portal-lines>
      <Table
        head={
          <tr>
            <th>What it is for</th>
            {anyPeriod ? <th>Period</th> : null}
            <th className="num">Amount</th>
          </tr>
        }
      >
        {lines.map((line, index) => {
          const period = periodOf(line);
          return (
            <tr key={`${line.type}-${index}`}>
              {/* The words the agency chose, exactly as they were sent. */}
              <td className="font-medium">{line.description}</td>
              {anyPeriod ? (
                <td className="text-ink-500">
                  {period ? period.filter(Boolean).map((day) => formatDate(day)).join(" – ") : ""}
                </td>
              ) : null}
              <td className="num tabular">{money(line.amount)}</td>
            </tr>
          );
        })}
      </Table>
    </div>
  );
}

/** What state an invoice is in, in a word the reader already uses rather than the one the column holds. */
function InvoiceState({ invoice }: { invoice: PortalInvoiceRow }) {
  const left = balanceOf(invoice);
  if (left <= 0) return <Badge tone="success">Paid</Badge>;
  if (invoice.status === "overdue") return <Badge tone="danger">Overdue</Badge>;
  if (invoice.paid > 0) return <Badge tone="warn">Part paid</Badge>;
  return <Badge tone="muted">Due</Badge>;
}

/* --------------------------------------------------------------- the screen */

export function PortalInvoice() {
  const { id } = useParams();
  const { get, loading, me, token } = usePortal();
  const [invoices, setInvoices] = useState<PortalInvoiceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    if (loading || !me) return;
    let cancelled = false;
    get<{ invoices: PortalInvoiceRow[] }>("/invoices")
      .then((value) => {
        if (!cancelled) setInvoices(value.invoices ?? []);
      })
      .catch((problem: unknown) => {
        if (!cancelled) setError(problem instanceof Error ? problem.message : "That could not be opened.");
      });
    return () => {
      cancelled = true;
    };
  }, [get, loading, me]);

  const invoice = (invoices ?? []).find((row) => row.id === id) ?? null;
  usePageTitle(invoice ? `Invoice ${invoice.number}` : "Your invoice");

  const currency = me?.agency?.currency ?? "NGN";
  const money = (minor: number) => formatMoney(Number(minor), currency);

  /*
    The file is fetched, not linked to — the same reason `PortalAgreement.tsx` fetches its copy.

    A portal session is a bearer token in `localStorage`, deliberately: a tenant may well be signed
    into their own agency's application in the same browser, and one cookie name cannot mean two
    things. The consequence is that an `<a href>` carries no credential at all, so a plain download
    link would answer 401 for the very person the invoice is addressed to. The bytes are fetched
    with the token, handed to the browser as a blob, and the object URL revoked immediately after.
  */
  async function download() {
    if (!invoice) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const response = await fetch(`/api/portal/invoices/${invoice.id}.pdf`, {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error("That copy could not be prepared. Try again.");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Invoice ${invoice.number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (problem) {
      setDownloadError(problem instanceof Error ? problem.message : "That copy could not be prepared.");
    } finally {
      setDownloading(false);
    }
  }

  /* Somebody whose session has expired, which is ordinary on a screen reached from an email months
     after it was sent. Not an error, so it is not dressed as one. */
  if (!loading && !me) return <Navigate to="/portal/sign-in" replace />;

  if (error || (invoices && !invoice)) {
    return (
      <PortalShell agency={me?.agency}>
        <NotTheirs message={error} me={me} />
      </PortalShell>
    );
  }

  if (!invoice) {
    return (
      <PortalShell agency={me?.agency}>
        <div className="flex items-center gap-3">
          <Spinner />
          <p className="text-sm text-ink-500">Loading…</p>
        </div>
      </PortalShell>
    );
  }

  const left = balanceOf(invoice);

  return (
    <PortalShell agency={me?.agency}>
      <div className="mb-5">
        <Link to="/portal" className="link text-sm">
          ← Your account
        </Link>
        <h1 className="mt-2 flex items-center gap-2 text-2xl font-semibold">
          <IconInvoice className="size-6 text-ink-400" />
          Invoice {invoice.number}
        </h1>
        <p className="mt-1 text-sm text-ink-500">
          {[invoice.unitLabel, invoice.propertyName].filter(Boolean).join(" · ") || "On your account"}
        </p>
      </div>

      {/* The question first, as everywhere else in the portal: what is still owed on this one. */}
      <Card className="mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Left to pay</p>
            <p className={`tabular mt-1 text-3xl font-semibold ${left > 0 ? "text-danger-700" : "text-success-700"}`}>
              {left > 0 ? money(left) : "Nothing"}
            </p>
            <p className="mt-1 text-sm text-ink-500">
              {left > 0 ? `Due ${formatDate(invoice.dueDate)}` : "This invoice has been settled in full."}
            </p>
          </div>
          <InvoiceState invoice={invoice} />
        </div>

        {/* The pay link is the way a tenant actually pays, and it is the same page the reminder
            sends them to. Offered only while there is something to pay — a button on a settled
            invoice invites somebody to pay it twice. */}
        {invoice.payToken && left > 0 ? (
          <a className="btn btn-primary mt-4" href={`/pay/${invoice.payToken}`}>
            Pay this invoice
          </a>
        ) : null}
      </Card>

      <Card className="mb-5" padded={false}>
        <div className="p-4 sm:p-5">
          <CardTitle hint="Every charge on this invoice, in the words your agency used.">What this is for</CardTitle>
        </div>
        <InvoiceLineTable lines={invoice.lines ?? []} money={money} />
        <dl className="border-t border-ink-200 px-4 py-2 sm:px-5">
          <Detail label="Total">{money(invoice.total)}</Detail>
          {invoice.paid > 0 ? <Detail label="Paid so far">{money(invoice.paid)}</Detail> : null}
          <Detail label="Left to pay">{left > 0 ? money(left) : "Nothing"}</Detail>
        </dl>
      </Card>

      {/*
        Their own copy, as a file.

        An invoice existed here only as a screen, so a tenant asked to send it on — to whoever
        actually pays the rent, to an employer, to their own file — had nothing to send. It is the
        same document the agency holds, drawn by the same code, and it leads with their agency's
        mark because the money is the agency's and not ours. ADR-0148.
      */}
      <Card className="mb-5">
        <CardTitle hint="The same document your agency holds, as a file you can keep or send on.">
          Keep a copy
        </CardTitle>
        <Button variant="secondary" onClick={download} disabled={downloading} data-portal-invoice-download>
          {downloading ? "Preparing…" : "Download the PDF"}
        </Button>
        {downloadError ? (
          <div className="mt-3">
            <Alert tone="danger">{downloadError}</Alert>
          </div>
        ) : null}
        <p className="mt-2 text-xs text-ink-500">
          Your copy is fetched with your sign-in, so a copy of this address is no use to anybody else.
        </p>
      </Card>

      {me?.agency ? (
        <p className="text-sm text-ink-500">
          If anything here looks wrong, speak to {me.agency.name}
          {me.agency.phone ? ` on ${me.agency.phone}` : ""}
          {me.agency.email ? ` or at ${me.agency.email}` : ""}.
        </p>
      ) : null}
    </PortalShell>
  );
}

/**
 * An id that is not on this account.
 *
 * Which is the same answer as an id that does not exist, deliberately: the portal never tells one
 * person whether another person's invoice is real. It reads as a wrong turn rather than as a
 * refusal, because it usually is one — an old link, or a number typed from a paper copy.
 */
function NotTheirs({ message, me }: { message: string | null; me: PortalMe | null }) {
  return (
    <Card>
      <h1 className="text-xl font-semibold">Not found</h1>
      <div className="mt-3">
        <Alert tone="danger">{message ?? "That invoice is not on this account."}</Alert>
      </div>
      <Link to="/portal" className="btn btn-secondary mt-5 w-full">
        Back to your account
      </Link>
      {me?.agency ? <p className="mt-3 text-sm text-ink-500">Or ask {me.agency.name} for the right link.</p> : null}
    </Card>
  );
}

export function PortalInvoicePage() {
  return (
    <PortalProvider>
      <PortalInvoice />
    </PortalProvider>
  );
}
